import { FaroTableBulkActions } from "@components/common/faro-table/faro-table-bulk-actions";
import { SkeletonProjectListItemCard } from "@components/common/mobile/skeleton-project-list-item-card";
import { ProjectListItemCardMobile } from "@components/project/project-list-item-card-mobile";
import { VirtualizedGrid } from "@components/virtualized/virtualized-grid";
import { PaginatedProjects } from "@custom-types/project-types";
import { Box, Divider } from "@mui/material";
import { CreateProjectDialog } from "@pages/projects/create-project-dialog";
import { useProjectMenuOptions } from "@pages/projects/use-project-menu-options";
import { generateProjectDetailsRoute } from "@router/router-helper";
import { selectedSdbCompanySelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { useProjectsPageBulkActions } from "@pages/projects/use-projects-page-bulk-actions";

type Props = Pick<
  PaginatedProjects,
  "projects" | "sortBy" | "projectArchivingState" | "loadMoreProjects"
> & {
  /** Whether the data for showing a table is loading or not */
  isLoading: boolean;

  /**
   * Flag to determine whether the "Create Project" button should be shown in the mobile view.
   *
   * @default true
   */
  shouldShowCreateButton?: boolean;
};

/**
 * Mobile-specific view for projects list with action bar (search, sort, export) and card layout.
 */
export function ProjectsMobileView({
  projects,
  sortBy,
  isLoading,
  projectArchivingState,
  loadMoreProjects,
  shouldShowCreateButton = true,
}: Props): JSX.Element | null {
  const menuItems = useProjectMenuOptions({
    contextType: "projects-menu-option-mobile",
  });
  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));
  const selectedSdbCompany = useAppSelector(selectedSdbCompanySelector);

  const isAnyRowSelected = selectedEntities.length !== 0;

  // The available bulk actions to display according to the archiving status and table context.
  const bulkActionsButtons = useProjectsPageBulkActions({
    companyId: selectedSdbCompany?.id ?? "",
    projects,
    projectStatus: projectArchivingState,
    subjectType: "workspace",
  });

  if (!selectedSdbCompany) {
    return null;
  }

  const allProjectIds = projects.map((project) => project.id);

  return (
    <VirtualizedGrid
      // The `key` here forces React to remount the grid whenever `sortBy` changes.
      // This ensures that the scroll position resets and the grid re-renders from the first item
      key={sortBy}
      items={projects}
      isLoading={isLoading}
      loadMoreItems={loadMoreProjects}
      renderSkeletonItem={(index) => (
        <Box key={index}>
          <SkeletonProjectListItemCard />
        </Box>
      )}
      estimateSize={112}
      renderItem={(project, index) => {
        return (
          <Box key={project.id}>
            {index !== 0 && <Divider />}

            <ProjectListItemCardMobile
              project={project}
              sortBy={sortBy}
              contextType="projects-menu-option-mobile"
              href={generateProjectDetailsRoute(
                selectedSdbCompany.id,
                project.id
              )}
              menuItems={menuItems}
            />
          </Box>
        );
      }}
      shouldRenderFooterOutside={isAnyRowSelected}
      renderFooterAction={() => (
        <>
          <FaroTableBulkActions
            bulkActionsButtons={bulkActionsButtons}
            numberOfSelectedRows={selectedEntities.length}
            isLoading={isLoading}
            allRowIds={allProjectIds}
          />
          {!isAnyRowSelected && shouldShowCreateButton && (
            <CreateProjectDialog />
          )}
        </>
      )}
    />
  );
}
